import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import { apiConfigs } from "../../constants/apiConfigs";
import { isFresh } from "../../utils/helpers";
import { ERROR, errors } from "../../constants/errors";
import { fetchBasicPost, fetchBasicSocialPost, fetchPost, fetchSocialPost } from "../../services/api/influencer";
import { BasicSocialPost, SocialPost } from "../../types/collaborations/CollaborationTypes";

export interface PostsSlice {
    loading: boolean,
    items: Record<string, { post: any, last_fetched_at: number }>,
    social_posts: Record<string, //SocialAccountId
        Record<string, //SocialPostId
            { post: SocialPost, last_fetched_at: number }
        >>,
    basics: Record<string, { post: any, last_fetched_at: number }>
    basic_social_posts: Record<string, {
        post: BasicSocialPost,
        last_fetched_at: number
    }>,
    error: string | null,
    error_code: number | null
}

export const initialState: PostsSlice = {
    loading: false,
    items: {},
    basics: {},
    error: null,
    error_code: null,
    basic_social_posts: {},
    social_posts: {}
}

const STALE_TIME = apiConfigs.FETCH_INFLUENCER_POST.staleTime;


export const fetchInfluencerPost = createAsyncThunk(
    'content/fetchInfluencerPost',
    async (postId: string, { getState, rejectWithValue }) => {
        const state = getState() as { posts: PostsSlice };
        if (postId in state.posts.items &&
            isFresh(state.posts.items[postId].last_fetched_at, STALE_TIME)
        ) {
            return rejectWithValue(ERROR.CACHED);
        }

        try {
            const post = await fetchPost(postId);
            return { post, postId };
        } catch (error) {
            return rejectWithValue(ERROR.NOT_FOUND);
        }

    }
)

export const fetchBasicPostThunk = createAsyncThunk(
    'content/fetchBasicPost',
    async (postId: string, { getState, rejectWithValue }) => {
        const state = getState() as { posts: PostsSlice };
        if (postId in state.posts.basics &&
            isFresh(state.posts.basics[postId].last_fetched_at, STALE_TIME)
        ) {
            return rejectWithValue(ERROR.CACHED);
        }

        try {
            const post = await fetchBasicPost(postId);
            return { post, postId };
        } catch (error) {
            return rejectWithValue(ERROR.NOT_FOUND);
        }

    }
)

export const fetchBasicSocialPostThunk = createAsyncThunk(
    'content/basic-social-post',
    async (postId: string, { getState, rejectWithValue }) => {
        const state = getState() as { posts: PostsSlice };
        if (postId in state.posts.basic_social_posts &&
            isFresh(state.posts.basic_social_posts[postId].last_fetched_at, STALE_TIME)
        ) {
            return rejectWithValue(ERROR.CACHED);
        }

        try {
            const post = await fetchBasicSocialPost(postId);
            return { post, postId };
        } catch (error) {
            return rejectWithValue(ERROR.NOT_FOUND);
        }

    }
)

export const fetchSocialPostThunk = createAsyncThunk(
    'social-account/social-post',
    async ({socialPostId, socialAccountId}: {socialPostId: string, socialAccountId: string}, { getState, rejectWithValue }) => {
        const state = getState() as { posts: PostsSlice };
        const staleTime = apiConfigs.FETCH_SOCIAL_POST.staleTime;
        if (socialAccountId in state.posts.social_posts && socialPostId in state.posts.social_posts[socialAccountId] &&
            isFresh(state.posts.social_posts[socialAccountId][socialPostId].last_fetched_at, staleTime)
        ) {
            return rejectWithValue(ERROR.CACHED);
        }

        try {
            const post: SocialPost = await fetchSocialPost(socialAccountId, socialPostId);
            return { post, socialPostId, socialAccountId };
        } catch (error) {
            return rejectWithValue(ERROR.NOT_FOUND);
        }

    }
)


const postsSlice = createSlice({
    name: 'posts',
    initialState,
    reducers: {
        markExpired: (state, data) => {
            const postId = data.payload;
            if (postId && postId in state.items) {
                state.items[postId] = { ...state.items[postId] };
            }
        }
    },
    extraReducers: (builder) => {
        builder
            .addCase(fetchInfluencerPost.pending, (state) => {
                state.loading = true;
                state.error = null;
                state.error_code = null
            })
            .addCase(fetchInfluencerPost.fulfilled, (state, action) => {
                state.items[action.payload.postId] = { ...action.payload, last_fetched_at: Date.now() }
                state.loading = false;
            })
            .addCase(fetchInfluencerPost.rejected, (state, action) => {
                state.loading = false;
                if (action.payload !== ERROR.CACHED) {
                    switch (action.payload) {
                        case ERROR.NOT_FOUND:
                        default:
                            state.error_code = errors[ERROR.NOT_FOUND].code;
                            state.error = errors[ERROR.NOT_FOUND].message;
                    }
                }
            })
            .addCase(fetchBasicPostThunk.fulfilled, (state, action) => {
                state.basics[action.payload.postId] = { ...action.payload, last_fetched_at: Date.now() }
                state.loading = false;
            })
            .addCase(fetchBasicSocialPostThunk.fulfilled, (state, action) => {
                const { postId, post } = action.payload;
                state.basic_social_posts[postId] = { post, last_fetched_at: Date.now() }
                state.loading = false;
            })
            .addCase(fetchSocialPostThunk.pending, (state) => {
                state.loading = true;
                state.error = null;
                state.error_code = null
            })
            .addCase(fetchSocialPostThunk.fulfilled, (state, action) => {
                const { socialAccountId, socialPostId, post } = action.payload;
                if (!(socialAccountId in state.social_posts)) {
                    state.social_posts[socialAccountId] = {};
                }
                state.social_posts[socialAccountId][socialPostId] = { post, last_fetched_at: Date.now() };
                state.loading = false;
            })
            .addCase(fetchSocialPostThunk.rejected, (state, action) => {
                state.loading = false;
                if (action.payload !== ERROR.CACHED) {
                    switch (action.payload) {
                        case ERROR.NOT_FOUND:
                        default:
                            state.error_code = errors[ERROR.NOT_FOUND].code;
                            state.error = errors[ERROR.NOT_FOUND].message;
                    }
                }
            })
    }
})

export const { markExpired } = postsSlice.actions;


export default postsSlice.reducer
